<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useUsage } from "../composables/useUsage";
import { formatAggregateDuration as formatDuration } from "../utils/format";

const { usage, usageError, usageLoading, loadUsage } = useUsage();

onMounted(() => {
  void loadUsage();
});

const isEmpty = computed(
  () => usage.value !== null && usage.value.current.videos_processed === 0,
);

const showPrevious = computed(
  () => usage.value !== null && usage.value.previous.videos_processed > 0,
);
</script>

<template>
  <aside
    class="rounded-lg border border-border bg-bg-elevated p-4 space-y-3"
    data-testid="usage-sidebar"
  >
    <header>
      <h3 class="text-sm font-semibold text-text-primary">Your work</h3>
      <p
        v-if="usage"
        class="text-xs text-text-secondary"
        data-testid="usage-window-label"
      >
        {{ usage.current.label }}
      </p>
    </header>

    <!-- Loading: keep the header visible so the widget doesn't jump on first paint. -->
    <p
      v-if="usageLoading && !usage"
      class="text-xs text-text-tertiary"
      data-testid="usage-loading"
    >
      Loading…
    </p>

    <p
      v-else-if="usageError"
      class="text-xs text-warning"
      data-testid="usage-error"
    >
      {{ usageError }}
    </p>

    <!-- Empty state: a new user / fresh month. -->
    <p
      v-else-if="isEmpty"
      class="text-xs text-text-tertiary"
      data-testid="usage-empty"
    >
      No videos processed yet this month.
    </p>

    <!-- Populated state: three result-shaped metrics. -->
    <div v-else-if="usage" class="space-y-3" data-testid="usage-metrics" data-content-state="populated">
      <div>
        <div
          class="text-xl font-semibold text-text-primary"
          data-testid="usage-videos-count"
        >
          {{ usage.current.videos_processed }}
        </div>
        <div class="text-xs text-text-secondary">videos processed</div>
      </div>
      <div>
        <div
          class="text-xl font-semibold text-text-primary"
          data-testid="usage-duration"
        >
          {{ formatDuration(usage.current.video_duration_ms) }}
        </div>
        <div class="text-xs text-text-secondary">of video</div>
      </div>
      <div>
        <div
          class="text-xl font-semibold text-text-primary"
          data-testid="usage-segments"
        >
          {{ usage.current.segments_found }}
        </div>
        <div class="text-xs text-text-secondary">rallies found</div>
      </div>
    </div>

    <!-- Final fall-through. Catches the "loadUsage was never called"
         degraded state (composable initialised but no fetch invoked).
         Without this, all four v-if branches would be false and the
         aside renders only the header — silent UX degradation flagged
         in the PR #814 silent-failure review. -->
    <p
      v-else
      class="text-xs text-text-tertiary"
      data-testid="usage-fallback"
    >
      Loading…
    </p>

    <!-- Last-month line: hidden when previous = 0 (zero-state honesty). -->
    <footer
      v-if="showPrevious && usage"
      class="pt-3 border-t border-border text-xs text-text-tertiary"
      data-testid="usage-last-month"
    >
      {{ usage.previous.label.split(" ")[0] }}:
      {{ usage.previous.videos_processed }} videos ·
      {{ formatDuration(usage.previous.video_duration_ms) }}
    </footer>
  </aside>
</template>
